/**
 * ★ A RECONCILIAÇÃO do identificador — [spec-008 T040b], US-4/US-5, N21.
 * Oitavo irmão de `transporte-tempo-real.js`: função pura, sem rede e sem
 * estado. Entra a demanda TIPADA do provedor mais o turno transcrito do
 * consultor, sai o veredito "pode consultar" ou "pergunte".
 *
 * ## ★ Quem preenche `arguments` é o `gpt-5.1` do PROVEDOR, não o modelo de voz
 *
 * No modo `responses`, o roteador que monta a `function_call` é um segundo
 * modelo — e nome vindo dele é **palpite**, não chave. Medido: `Teste Alfa` →
 * `"Teste Alpha"` / `"Teste Z"` / `"cliente teste alpha"` em **7 de 8**
 * medições (G-12 §6 + G-13 §3).
 *
 * A [T032] já fechou a metade dura disso: o que vira consulta é o TURNO
 * TRANSCRITO, e `arguments` viaja em `desambiguacao`, marcado como pista. Falta
 * a outra metade, que é esta: **quando a pista contradiz a fala, não se
 * adivinha — pergunta-se.**
 *
 * ## Por que o discriminante é a fala, e não uma busca
 *
 * "Reconciliar contra a entidade real" pede um lookup, e o navegador não tem
 * como fazê-lo: quem resolve cliente é o servidor, no `POST /chat`. O que ESTE
 * lado pode fazer — e é o que decide o caso medido — é recusar que um nome que
 * o consultor **nunca disse** atravesse como se fosse a chave. `Teste Alpha`
 * não aparece em "qual o PL do cliente Teste Alfa"; a correspondência não é
 * exata, e aí a voz pergunta.
 *
 * ★ **Um valor correto atribuído ao papel errado é pior que um valor errado** —
 * o primeiro parece confiável (D-13). Por isso a recusa é o desfecho seguro.
 *
 * ## ⚠️ O escopo é ESTREITO de propósito, e a lacuna está declarada
 *
 * Só argumentos cuja CHAVE nomeia uma entidade (`cliente`, `ativo`, `conta`, …)
 * são reconciliados. Dois motivos:
 *
 * 1. **O que não é identificador não vira lookup.** Pela N21 (T032) a consulta
 *    sai do turno falado; um `periodo: "mensal"` parafraseado nunca chega a ser
 *    chave de nada. Reconciliar TODO valor de string faria a voz perguntar em
 *    quase todo turno — e guarda que atrapalha sempre é guarda que some do
 *    código em uma semana.
 * 2. **O identificador é o DISCRIMINANTE da entidade**, que é exatamente o que
 *    o N16 protege: `NTN-B 2035` → `NTN-B` mantém o número e apaga o ano.
 *
 * **Lacuna declarada:** chave de identificador com nome fora desta lista (um
 * `beneficiario` novo, por exemplo) NÃO é reconciliada. Ela é nomeada aqui em
 * vez de fingida — e quem acrescentar o conceito acrescenta a chave.
 */
import { normalizeText } from '@utils/Format'

/**
 * As CHAVES de argumento que carregam identificador de entidade. Casam por
 * SUBSTRING no nome da chave (`nome_cliente`, `client_name`, `id_conta` casam
 * com `cliente`/`client`, `conta`), porque o catálogo de tools vive no servidor
 * e o front não o duplica.
 */
export const CHAVES_DE_IDENTIFICADOR = Object.freeze([
  'cliente',
  'client',
  'ativo',
  'asset',
  'papel',
  'ticker',
  'fundo',
  'conta',
  'account',
  'escritorio',
  'profissional',
  'cnpj',
  'cpf',
])

/** Valor curto demais para discriminar entidade nenhuma — não se reconcilia. */
const MINIMO_DE_CARACTERES = 3

/**
 * Os dois motivos de recusa, agora SEPARADOS — a correção do owner de
 * 17/09/2026 é exatamente esta distinção:
 *
 * | motivo | exemplo | por quê |
 * |---|---|---|
 * | `MOTIVO_NAO_FOI_FALADO` | `Teste Alfa` → `"Teste Z"` | o nome não está na fala nem com outra grafia |
 * | `MOTIVO_DISCRIMINANTE_PERDIDO` | `NTN-B 2035` → `"NTN-B"` | a pista apagou o que separa um papel do outro |
 *
 * E o caso que **deixou de ser recusa**: `Teste Alfa` → `"Teste Alpha"`.
 */
export const MOTIVO_NAO_FOI_FALADO = 'nao_foi_falado'
export const MOTIVO_DISCRIMINANTE_PERDIDO = 'discriminante_perdido'

const _ehChaveDeIdentificador = (chave) => {
  const nome = normalizeText(String(chave || ''))
  if (!nome) return false
  return CHAVES_DE_IDENTIFICADOR.some((conceito) => nome.indexOf(conceito) !== -1)
}

/**
 * Normaliza para COMPARAR: sem acento, sem caixa, e com tudo que não é letra ou
 * número virando espaço. É o que faz `Teste-Alfa`, `teste alfa` e `Teste  Alfa`
 * corresponderem.
 */
const _paraComparar = (texto) =>
  normalizeText(String(texto || ''))
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const _tokens = (texto) => _paraComparar(texto).split(' ').filter(Boolean)

const _ehNumero = (token) => /^[0-9]+$/.test(token)

/** Levenshtein — curto e sem dependência; os tokens aqui têm poucas letras. */
const _distancia = (a, b) => {
  const linha = []
  for (let j = 0; j <= b.length; j += 1) linha[j] = j
  for (let i = 1; i <= a.length; i += 1) {
    let anterior = linha[0]
    linha[0] = i
    for (let j = 1; j <= b.length; j += 1) {
      const guardado = linha[j]
      const custo = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1
      linha[j] = Math.min(linha[j] + 1, linha[j - 1] + 1, anterior + custo)
      anterior = guardado
    }
  }
  return linha[b.length]
}

/** Quanto de deriva ortográfica se tolera, em fração do token mais longo. */
const TOLERANCIA_DE_GRAFIA = 0.4

/**
 * ★ [correção do owner, 17/09/2026] **Variação de GRAFIA não é divergência.**
 *
 * *"Esse erro de grafia não é um problema, é um detalhe, tá tudo certo"* —
 * sobre `Teste Alfa` → `"Teste Alpha"`. E ele está certo: transliteração não é
 * palpite, é ortografia; a entidade é a mesma escrita de outro jeito.
 *
 * ⚠️ **NÚMERO não tem variante de grafia.** `2035` e `2030` não são o mesmo
 * ano escrito diferente — são papéis diferentes. Token numérico casa EXATO ou
 * não casa, e é isso que mantém o N16 de pé enquanto a letra afrouxa.
 *
 * O risco de ser generoso aqui é baixo por construção: pela N21 o que vira
 * consulta é o TURNO TRANSCRITO, nunca este valor. Aceitar demais só deixa a
 * consulta seguir com a fala do consultor — que é o comportamento certo.
 * Recusar demais é que quebrava a feature, e foi o que o owner viu.
 */
const _grafiaCompativel = (a, b) => {
  if (a === b) return true
  if (_ehNumero(a) || _ehNumero(b)) return false
  const limite = Math.round(Math.max(a.length, b.length) * TOLERANCIA_DE_GRAFIA)
  if (limite < 1) return false
  return _distancia(a, b) <= limite
}

/**
 * Onde, no turno falado, começa o trecho que corresponde a `procurados` —
 * token a token, tolerante à grafia. `-1` quando não corresponde a lugar
 * nenhum.
 */
const _ondeCasa = (procurados, ditos) => {
  for (let i = 0; i + procurados.length <= ditos.length; i += 1) {
    let casou = true
    for (let j = 0; j < procurados.length; j += 1) {
      if (!_grafiaCompativel(procurados[j], ditos[i + j])) {
        casou = false
        break
      }
    }
    if (casou) return i
  }
  return -1
}

/**
 * ★ O veredito. `ok: false` quando ALGUM identificador da pista não aparece no
 * que o consultor falou.
 *
 * @param {Object} params
 * @param {{argumentos?: Object}} params.demanda a demanda TIPADA do provedor
 * @param {string} params.turno o turno transcrito do consultor
 * @returns {{ok: boolean, chave?: string}} — o `chave` é para o REGISTRO da
 *   recusa (erro nomeado); o VALOR divergente nunca sobe junto, porque ele é
 *   justamente o palpite que não se quer repetir.
 */
/**
 * O veredito de UM argumento. `null` quando ele não tem o que reconciliar
 * (não é identificador, não é string, é curto demais) — que é o caso comum.
 */
const _vereditoDoArgumento = (chave, valor, ditos) => {
  if (typeof valor !== 'string') return null
  if (!_ehChaveDeIdentificador(chave)) return null
  const procurados = _tokens(valor)
  if (_paraComparar(valor).length < MINIMO_DE_CARACTERES) return null

  // Não aparece na fala de jeito nenhum — nem com outra grafia. Isto NÃO é
  // `Alfa`/`Alpha`: é um nome que o consultor nunca disse (o `"Teste Z"` das
  // medições), e aí a voz pergunta em vez de adivinhar.
  const onde = _ondeCasa(procurados, ditos)
  if (onde === -1) return { ok: false, chave, motivo: MOTIVO_NAO_FOI_FALADO }

  // ★ [correção do owner] O DISCRIMINANTE PERDIDO, que continua importando:
  // o consultor disse `NTN-B 2035` e a pista trouxe só `NTN-B`. O ano é o que
  // separa um papel do outro; sem ele, dois ativos viram o mesmo. Não é
  // grafia — é ambiguidade real, e perguntar é melhor que adivinhar.
  const seguinte = ditos[onde + procurados.length]
  if (seguinte && _ehNumero(seguinte) && !procurados.some(_ehNumero)) {
    return { ok: false, chave, motivo: MOTIVO_DISCRIMINANTE_PERDIDO }
  }
  return null
}

export const reconciliarIdentificador = ({ demanda, turno } = {}) => {
  const argumentos = (demanda && demanda.argumentos) || {}
  const ditos = _tokens(turno)
  const chaves = Object.keys(argumentos)
  for (let i = 0; i < chaves.length; i += 1) {
    const veredito = _vereditoDoArgumento(chaves[i], argumentos[chaves[i]], ditos)
    if (veredito) return veredito
  }
  return { ok: true }
}
